import pp3Icon from "../../../assets/pp3-icon.png";
import dayFocusIcon from "../../../assets/dayfocus-icon.png";
import susLinkIcon from "../../../assets/suslink-icon.png";
import retroPocketToysIcon from "../../../assets/retropockettoys-icon.png";
import pp3ShotPresets from "../../../assets/pp3-shot-presets.png";
import pp3ShotEditor from "../../../assets/pp3-shot-editor.png";
import dayFocusShotFocus from "../../../assets/dayfocus-shot-focus.png";
import dayFocusShotProgress from "../../../assets/dayfocus-shot-progress.png";
import rptShotDrawer from "../../../assets/retropockettoys-shot-drawer.png";
import rptShotRingToss from "../../../assets/retropockettoys-shot-ringtoss.png";
import rptShotTicTacToe from "../../../assets/retropockettoys-shot-tictactoe.png";
import rptShotHoops from "../../../assets/retropockettoys-shot-hoops.png";
import rptShotLeapfrog from "../../../assets/retropockettoys-shot-leapfrog.png";

export interface ProductFeature {
  title: string;
  body: string;
}

export interface ProductScreenshot {
  src: string;
  alt: string;
  caption: string;
}

export interface Product {
  id: string;
  name: string;
  /**
   * Which hub the product belongs to. Games and apps are listed separately because
   * they are found by different people looking for different things.
   */
  kind: "app" | "game";
  path: string;
  platforms: string;
  icon: string;
  cardSummary: string;
  hubSummary: string;
  lede: string;
  /** Empty while a product is still unreleased. */
  appStoreUrl: string;
  privacyPath: string;
  features: ProductFeature[];
  screenshots: ProductScreenshot[];
  /**
   * Set while the app is not on the App Store yet. To release: set this to false (or
   * delete the line), put the real App Store URL in appStoreUrl, and add screenshots.
   * Nothing else needs changing — the page and the apps hub both read this flag.
   */
  comingSoon?: boolean;
  /**
   * Shown on the detail page while `comingSoon` is set. Optional: without it the
   * page falls back to a line that is true of any unreleased product. Set it when
   * there is something more specific and accurate to say.
   */
  comingSoonNote?: string;
}

export const picPerfect3: Product = {
  id: "pic-perfect-3",
  kind: "app",
  name: "Pic Perfect 3",
  path: "/apps/pic-perfect-3",
  platforms: "iOS & iPadOS",
  icon: pp3Icon,
  cardSummary:
    "Resize any photo to the exact dimensions a platform expects — including Microsoft Teams and LinkedIn — and save it straight into a tidy album.",
  hubSummary:
    "Photo sizing built for people who care about the shot: presets for Microsoft Teams, LinkedIn and every other platform worth posting to, plus a proper in-app library so a failed camera-roll save never costs you a photo.",
  lede:
    "Exact-pixel photo sizing for the platforms people actually share to, with a save that always lands somewhere you can find it.",
  appStoreUrl: "https://apps.apple.com/us/app/pic-perfect-3/id6796856896",
  privacyPath: "/pp3-privacy.html",
  features: [
    {
      title: "Teams and LinkedIn presets",
      body:
        "Profile and banner sizes grouped and ready for Microsoft Teams and LinkedIn, alongside the rest of the social preset catalog.",
    },
    {
      title: "A save that does not fail",
      body:
        "Saving is tiered — a per-preset album first, then the camera roll, then the app's own library — so a permissions hiccup never means a lost edit.",
    },
    {
      title: "Built-in library",
      body:
        "Every export is kept in an in-app library with a stamped filename, so you can find, re-share, or clean up an image long after you made it.",
    },
    {
      title: "Pro is a one-time unlock",
      body:
        'A single "Pic Perfect Pro" purchase unlocks the full preset catalog, custom sizes, and batch export. No subscription.',
    },
  ],
  screenshots: [
    {
      src: pp3ShotPresets,
      alt: "Pic Perfect 3 preset picker",
      caption: "PRESET PICKER",
    },
    {
      src: pp3ShotEditor,
      alt: "Pic Perfect 3 editor",
      caption: "EDITOR",
    },
  ],
};

export const dayFocus: Product = {
  id: "dayfocus",
  kind: "app",
  name: "DayFocus",
  path: "/apps/dayfocus",
  platforms: "iOS, iPadOS & watchOS",
  icon: dayFocusIcon,
  cardSummary:
    "One task in front of you at a time. A calm, distraction-free way to work through the day — free, with an Apple Watch companion.",
  hubSummary:
    "A single-task focus app: one thing in front of you, a daily completed/total counter to keep momentum visible, a Rainy Day List for when you are at a loose end, and an Apple Watch companion.",
  lede:
    "One task in front of you at a time. A calm way to move through the day, with a daily progress counter and a watch companion for a glance.",
  appStoreUrl: "https://apps.apple.com/us/app/day-focus/id6758362229",
  privacyPath: "/dayfocus-privacy.html",
  features: [
    {
      title: "One task, front and center",
      body:
        "No overwhelming list — just the task you are on and what is queued behind it.",
    },
    {
      title: "Daily progress you can feel",
      body:
        "A simple completed and total count for the day, so momentum is visible at a glance.",
    },
    {
      title: "Rainy Day List",
      body:
        "Keep a separate list of things worth doing when you are bored, so an idle moment has somewhere to go.",
    },
    {
      title: "Apple Watch companion",
      body:
        "The current task and the day's progress stay one glance away, without unlocking your phone.",
    },
    {
      title: "Free, with an optional unlock",
      body:
        'Free with ads, and a one-time $0.99 "Remove Ads" purchase if you would rather not see them. No subscription.',
    },
  ],
  screenshots: [
    {
      src: dayFocusShotFocus,
      alt: "DayFocus current task screen",
      caption: "CURRENT FOCUS",
    },
    {
      src: dayFocusShotProgress,
      alt: "DayFocus daily progress counter",
      caption: "DAILY PROGRESS",
    },
  ],
};

export const susLink: Product = {
  id: "suslink",
  kind: "app",
  name: "SusLink",
  path: "/apps/suslink",
  platforms: "iOS & iPadOS",
  icon: susLinkIcon,
  cardSummary:
    "Paste a suspicious text and find out what is actually wrong with it — explained in plain English, entirely on your phone.",
  hubSummary:
    "A scam-text checker that explains itself. Share a suspicious message to SusLink and it tells you what it found and why, without sending anything anywhere — there is no server behind it at all.",
  lede:
    "See what a link really is. Share a suspicious message and SusLink explains what is wrong with it, in plain English, without sending anything anywhere.",
  appStoreUrl: "",
  privacyPath: "/suslink-privacy.html",
  comingSoon: true,
  comingSoonNote:
    "SusLink is finished and in testing. This page will link to the App Store the day it is released.",
  features: [
    {
      title: "It explains, it does not just block",
      body:
        "Every verdict names its reasons in plain English — a lookalike address, a company named in the message whose link goes somewhere else — with the part of the address that matters picked out. You learn the pattern, not just the answer.",
    },
    {
      title: "It reads the words, not just the link",
      body:
        "Most scam texts give themselves away in the writing long before the address does. An on-device model reads the message itself, which is where most of the detection comes from.",
    },
    {
      title: "Honest about what it misses",
      body:
        "Tested on public collections of real scam and ordinary messages, SusLink warned about roughly two thirds of scam texts and about one ordinary message in 125. It says so in the app, because a security app that will not tell you how often it is right is asking to be taken on faith.",
    },
    {
      title: "No server, no account, no data",
      body:
        "Every check runs on the phone, including the model. Nothing is sent anywhere because there is nowhere to send it — the app works in airplane mode, which you can test rather than take on trust.",
    },
    {
      title: "The block list is yours",
      body:
        "Nothing ships in it. It holds only the sites you looked at, read the explanation for, and decided were scams — and you can undo any of them.",
    },
  ],
  screenshots: [],
};

export const retroPocketToys: Product = {
  id: "retro-pocket-toys",
  kind: "game",
  name: "Retro Pocket Toys",
  path: "/games/retro-pocket-toys",
  platforms: "iOS",
  icon: retroPocketToysIcon,
  cardSummary:
    "The water-filled toys that lived in every drawer in the 1980s — ring toss, hoops, tic-tac-toe and leapfrog — simulated with real water rather than an animation.",
  hubSummary:
    "Four water games in one pocket: hold the rubber bellows and a jet of air lifts the pieces up through the tank. The water is simulated from the real properties of the toy, and lean the phone and it leans with you.",
  lede:
    "Squeeze the bellows, send the rings up through the water, and try to land them. Four of the water toys everybody had, built to behave like the real thing.",
  appStoreUrl:
    "https://apps.apple.com/us/app/retro-pocket-toys/id6809843731",
  privacyPath: "/retropockettoys-privacy.html",
  features: [
    {
      title: "Real water, not an animation",
      body:
        "The tank is simulated from the actual properties of the object — water at room temperature, and pieces moulded in a plastic slightly denser than it, which is why they sink slowly instead of floating or dropping. Rings, balls and counters all fall at different rates because their shapes carry different drag.",
    },
    {
      title: "A bellows, not a button",
      body:
        "Hold it and the jet builds. Keep holding and the puff runs out, the way a bellows does. Let go and it dies away — and touch it again before it reaches nothing and it climbs straight back from where it got to. Short taps, long holds and quick pumping all do different things.",
    },
    {
      title: "Lean it and the water leans",
      body:
        "Tilt the phone and the whole body of water drifts with it. It is a weak current on purpose: enough to walk a hovering ring sideways onto a peg, never enough to be a better way to play than the button.",
    },
    {
      title: "Four toys in the drawer",
      body:
        "Ring toss, hoops, tic-tac-toe and leapfrog. Each is a different board in the same tank, so they share the same water and none of them plays the same way. A ring has to go over the top of a peg and slide down it, which is exactly as difficult as you remember.",
    },
    {
      title: "It feels like an object",
      body:
        "Haptics that follow the squeeze rather than firing once, a moulded case with a crooked printed sticker, and pieces that vary slightly in weight the way anything off an injection line does.",
    },
    {
      title: "Try everything, then keep one",
      body:
        "Every toy is open for five plays. When those are spent you keep whichever one you liked best, for good and for nothing, or unlock all four with a single purchase. No subscription, no ads, and nothing collected.",
    },
  ],
  screenshots: [
    {
      src: rptShotDrawer,
      alt: "The Retro Pocket Toys drawer, showing all four toys",
      caption: "THE DRAWER",
    },
    {
      src: rptShotRingToss,
      alt: "Ring toss, with three rings landed on the pegs",
      caption: "RING TOSS",
    },
    {
      src: rptShotHoops,
      alt: "Hoops, with balls banked in the rack beside the hoop",
      caption: "HOOPS",
    },
    {
      src: rptShotTicTacToe,
      alt: "Tic-tac-toe, with two counters threaded onto posts in the grid",
      caption: "TIC-TAC-TOE",
    },
    {
      src: rptShotLeapfrog,
      alt: "Leapfrog, with counters resting on lily pads",
      caption: "LEAPFROG",
    },
  ],
};

export const products: Product[] = [
  picPerfect3,
  dayFocus,
  susLink,
  retroPocketToys,
];

/** The two hubs. Derived, so a product can never be listed in neither. */
export const apps: Product[] = products.filter((p) => p.kind === "app");
export const games: Product[] = products.filter((p) => p.kind === "game");
